// Distributor Query Tracker — distributors (and SE/ASM on their behalf) raise
// queries (ledger, transport, damage, claims…); SALES_COORDINATOR/SUPER_ADMIN
// answer in a chat-style thread with file attachments. One component, three
// shells: mode "portal" (distributor), "admin" (handlers + MANAGEMENT),
// "field" (SE/ASM/ZSM). What each caller may do comes from the per-query
// `perms` object the backend returns, never from the mode.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, MessagesSquare, MessageSquare, Paperclip, Download, Eye,
  X, Send, Play, CheckCircle2, Lock, RotateCcw, CalendarRange,
} from "lucide-react";
import { api, formatApiError, API_BASE, getToken } from "@/lib/api";
import { authenticatedDownload } from "@/components/bulk/useBulkJob";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";

const STATUS_STYLE = {
  open:        "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-sky-50 text-sky-700 border-sky-200",
  resolved:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  reopened:    "bg-rose-50 text-rose-700 border-rose-200",
  closed:      "bg-neutral-100 text-neutral-600 border-neutral-200",
};
const STATUS_LABEL = {
  open: "Open", in_progress: "In progress", resolved: "Resolved", reopened: "Reopened", closed: "Closed",
};
const STATUS_FILTERS = ["", "open", "in_progress", "resolved", "reopened", "closed"];

const ACCEPT = ".pdf,.xlsx,.xls,.docx,.doc,.jpg,.jpeg,.png,.webp";
const ALLOWED_EXT = new Set(ACCEPT.split(","));
const MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024;   // total of all files in one message
const PAGE_SIZE = 30;
// Backend caps page_size at 200; a silent refresh re-fetches at most this many pages in one call.
const MAX_REFRESH_PAGES = Math.floor(200 / PAGE_SIZE);
const ALL_REASONS = "__all";

function fmtSize(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

// Backend stores YYYY-MM-DD; field users read DD-MM-YYYY.
function fmtYmd(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-");
  return d && m && y ? `${d}-${m}-${y}` : ymd;
}

function extOf(name) {
  const i = (name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

function isViewable(att) {
  const ct = att?.content_type || "";
  return ct.startsWith("image/") || ct === "application/pdf" || /\.(pdf|jpe?g|png|webp)$/i.test(att?.filename || "");
}

// Validate a picked batch against what's already chosen; returns the merged list.
function mergeFiles(current, picked) {
  const next = [...current];
  let total = next.reduce((n, f) => n + f.size, 0);
  for (const f of picked) {
    if (next.length >= MAX_FILES) { toast.error(`Max ${MAX_FILES} files per message.`); break; }
    if (!ALLOWED_EXT.has(extOf(f.name))) { toast.error(`${f.name}: only PDF, Excel, Word or photos are allowed.`); continue; }
    if (f.size === 0) { toast.error(`${f.name} is empty.`); continue; }
    if (total + f.size > MAX_BYTES) { toast.error(`${f.name}: attachments can be at most 10 MB in total (you have ${fmtSize(total)} already).`); continue; }
    total += f.size;
    next.push(f);
  }
  return next;
}

async function viewAttachment(att) {
  try {
    const res = await fetch(`${API_BASE}/distributor-queries/attachments/${encodeURIComponent(att.id)}?inline=1`,
      { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) { toast.error("Could not open attachment"); }
}

async function downloadAttachment(att) {
  try {
    await authenticatedDownload(`/distributor-queries/attachments/${encodeURIComponent(att.id)}`, att.filename);
  } catch (e) { toast.error(`Download failed: ${e?.message || e}`); }
}

function StatusBadge({ status }) {
  return (
    <Badge className={"border text-[10px] whitespace-nowrap " + (STATUS_STYLE[status] || "")}>
      {STATUS_LABEL[status] || status}
    </Badge>
  );
}

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 };
// The web server in front of the app may only allow ~1 MB per request (413).
const SAFE_BATCH_BYTES = 900 * 1024;

// Shrinks a phone photo so it fits a small request (used only as a fallback).
async function shrinkImage(file) {
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type) || file.size <= 700 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    let scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    for (const quality of [0.72, 0.6, 0.5, 0.4]) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bmp.width * scale));
      canvas.height = Math.max(1, Math.round(bmp.height * scale));
      canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
      if (blob && blob.size <= 900 * 1024) {
        return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
      }
      scale *= 0.8;
    }
    return file;
  } catch { return file; }
}

// Sends text + files. Tries everything in one request (up to 10 MB total). If the
// server refuses the size (413), retries with photos shrunk and sent in small
// batches: text + first batch first, the rest as follow-up messages. Returns
// { data, partialError } — partialError is set if a later batch failed.
async function postWithSizeFallback(url, fields, files, followUrlFor) {
  const build = (obj, fs) => {
    const fd = new FormData();
    Object.entries(obj).forEach(([k, v]) => fd.append(k, v));
    fs.forEach((f) => fd.append("files", f));
    return fd;
  };
  try {
    return { data: (await api.post(url, build(fields, files), MULTIPART)).data, partialError: null };
  } catch (e) {
    if (e?.response?.status !== 413 || files.length === 0) throw e;
  }
  const small = await Promise.all(files.map(shrinkImage));
  const tooBig = small.find((f) => f.size > SAFE_BATCH_BYTES);
  if (tooBig) {
    throw new Error(`"${tooBig.name}" is too big to send right now. Please send a smaller file, or ask your admin to raise the server upload limit.`);
  }
  const batches = [];
  let cur = [], sum = 0;
  for (const f of small) {
    if (cur.length && sum + f.size > SAFE_BATCH_BYTES) { batches.push(cur); cur = []; sum = 0; }
    cur.push(f); sum += f.size;
  }
  if (cur.length) batches.push(cur);
  let data = (await api.post(url, build(fields, batches[0]), MULTIPART)).data;
  for (const b of batches.slice(1)) {
    try {
      data = (await api.post(followUrlFor(data), build({ body: "" }, b), MULTIPART)).data;
    } catch (e) {
      return { data, partialError: formatApiError(e) };
    }
  }
  return { data, partialError: null };
}

function FilePicker({ files, onChange, disabled, testid }) {
  const inputRef = useRef(null);
  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
        onChange={async (e) => {
          const picked = Array.from(e.target.files || []);
          e.target.value = "";
          if (picked.length) onChange(mergeFiles(files, picked));
        }}
        data-testid={`${testid}-input`}
      />
      <Button type="button" variant="outline" size="sm" disabled={disabled || files.length >= MAX_FILES}
              onClick={() => inputRef.current?.click()} data-testid={`${testid}-btn`}>
        <Paperclip className="h-4 w-4 mr-1.5" /> Attach files
        <span className="ml-1.5 text-[10px] text-neutral-400">({files.length}/{MAX_FILES} files · {fmtSize(files.reduce((n, f) => n + f.size, 0))} of 10 MB)</span>
      </Button>
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg border border-neutral-200 px-2 py-1 text-xs">
              <Paperclip className="h-3 w-3 text-neutral-400 shrink-0" />
              <span className="truncate flex-1 min-w-0">{f.name}</span>
              <span className="text-neutral-400 shrink-0">{fmtSize(f.size)}</span>
              <button type="button" aria-label={`Remove ${f.name}`} disabled={disabled}
                      onClick={() => onChange(files.filter((_, j) => j !== i))}
                      data-testid={`${testid}-remove-${i}`}>
                <X className="h-3.5 w-3.5 text-rose-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentChip({ att }) {
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs max-w-full"
         data-testid={`query-att-${att.id}`}>
      <Paperclip className="h-3 w-3 text-neutral-400 shrink-0" />
      <span className="truncate min-w-0 text-neutral-800">{att.filename}</span>
      <span className="text-neutral-400 shrink-0">{fmtSize(att.size_bytes)}</span>
      {isViewable(att) && (
        <button type="button" onClick={() => viewAttachment(att)} aria-label="View"
                className="p-0.5 text-[#6D2C91] shrink-0" data-testid={`query-att-view-${att.id}`}>
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      <button type="button" onClick={() => downloadAttachment(att)} aria-label="Download"
              className="p-0.5 text-[#6D2C91] shrink-0" data-testid={`query-att-download-${att.id}`}>
        <Download className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function NewQueryDialog({ open, onOpenChange, distributors, reasons, onCreated }) {
  const [distributorId, setDistributorId] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDistributorId(distributors.length === 1 ? distributors[0].id : "");
    setReasonCode(""); setFromDate(""); setToDate("");
    setSubject(""); setDescription(""); setFiles([]);
  }, [open, distributors]);

  const reason = reasons.find((r) => r.code === reasonCode);
  const needsDates = !!reason?.requires_date_range;

  const submit = async () => {
    if (!distributorId) { toast.error("Select the distributor this query is for."); return; }
    if (!reasonCode) { toast.error("Select a reason."); return; }
    if (needsDates) {
      if (!fromDate || !toDate) { toast.error("From and To dates are required for this reason."); return; }
      if (fromDate > toDate) { toast.error("From date must be on or before To date."); return; }
    }
    if (!subject.trim()) { toast.error("Enter a subject."); return; }
    if (!description.trim()) { toast.error("Describe your query."); return; }
    const fields = {
      distributor_id: distributorId, reason_code: reasonCode,
      subject: subject.trim(), description: description.trim(),
      ...(needsDates ? { from_date: fromDate, to_date: toDate } : {}),
    };
    setBusy(true);
    try {
      const { data, partialError } = await postWithSizeFallback(
        "/distributor-queries", fields, files,
        (d) => `/distributor-queries/${d.id}/messages`);
      toast.success(`Query ${data.query_no} raised`);
      if (partialError) toast.error(`Query was raised, but some attachments were not sent: ${partialError}`);
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6 grid-cols-1" data-testid="query-new-dialog">
        <DialogHeader>
          <DialogTitle>New query</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {distributors.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Distributor</Label>
              <SearchableSelect
                value={distributorId} onChange={setDistributorId}
                options={distributors.map((d) => ({ id: d.id, code: d.code, name: d.name }))}
                placeholder="Choose distributor…" testid="query-new-distributor"
              />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Reason</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger data-testid="query-new-reason">
                <SelectValue placeholder="What is this about?" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {needsDates && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">From date</Label>
                <Input type="date" value={fromDate} max={toDate || undefined}
                       onChange={(e) => setFromDate(e.target.value)} data-testid="query-new-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To date</Label>
                <Input type="date" value={toDate} min={fromDate || undefined}
                       onChange={(e) => setToDate(e.target.value)} data-testid="query-new-to" />
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Subject</Label>
            <Input value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)}
                   placeholder="e.g. Ledger for Q1" data-testid="query-new-subject" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea value={description} maxLength={4000} rows={4}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Give the details the team needs to help you"
                      data-testid="query-new-description" />
            <div className="text-[10px] text-neutral-400 text-right">{description.length}/4000</div>
          </div>
          <FilePicker files={files} onChange={setFiles} disabled={busy} testid="query-new-files" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="tulsi-gradient text-white" onClick={submit} disabled={busy} data-testid="query-new-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Raise query
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_COPY = {
  resolve: { title: "Mark as resolved?", confirm: "Mark resolved", placeholder: "Note for the distributor (optional)" },
  close:   { title: "Close this query?", confirm: "Close query", placeholder: "Note (optional)" },
  reopen:  { title: "Reopen this query?", confirm: "Reopen", placeholder: "Why is this not resolved? (required)" },
};

function QueryDetailDialog({ queryId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [actionDlg, setActionDlg] = useState(null); // "resolve" | "close" | "reopen"
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const endRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!queryId) { setDetail(null); return; }
    let cancelled = false;
    setDetail(null); setBody(""); setFiles([]); setActionDlg(null);
    setLoading(true);
    api.get(`/distributor-queries/${encodeURIComponent(queryId)}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch((e) => { if (!cancelled) { toast.error(formatApiError(e)); onCloseRef.current(); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [queryId]);

  const applyDetail = (d) => { setDetail(d); onChanged(); };

  // A 409 means someone else (or auto-close) changed the query; show the server's current state.
  const refetchOnConflict = (e) => {
    if (e?.response?.status !== 409) return;
    api.get(`/distributor-queries/${encodeURIComponent(detail.id)}`)
      .then((r) => applyDetail(r.data))
      .catch(() => {});
  };

  const send = async () => {
    if (!body.trim() && files.length === 0) { toast.error("Type a message or attach a file."); return; }
    setSending(true);
    try {
      const { data, partialError } = await postWithSizeFallback(
        `/distributor-queries/${detail.id}/messages`, { body: body.trim() }, files,
        () => `/distributor-queries/${detail.id}/messages`);
      if (partialError) toast.error(`Some attachments were not sent: ${partialError}`);
      setBody(""); setFiles([]);
      applyDetail(data);
      setTimeout(() => endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e)); refetchOnConflict(e); }
    finally { setSending(false); }
  };

  const runAction = async (action, actionNote) => {
    setActing(true);
    try {
      const payload = { action };
      if (actionNote && actionNote.trim()) payload.note = actionNote.trim();
      const { data } = await api.post(`/distributor-queries/${detail.id}/status`, payload);
      toast.success(`Query ${STATUS_LABEL[data.status] ? STATUS_LABEL[data.status].toLowerCase() : "updated"}`);
      setActionDlg(null);
      applyDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 409) setActionDlg(null);
      refetchOnConflict(e);
    }
    finally { setActing(false); }
  };

  const confirmAction = () => {
    if (actionDlg === "reopen" && !note.trim()) { toast.error("Tell us why you are reopening it."); return; }
    runAction(actionDlg, note);
  };

  const perms = detail?.perms || {};
  const hasActions = perms.can_start || perms.can_resolve || perms.can_close || perms.can_reopen;

  return (
    <>
      <Dialog open={!!queryId} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6 gap-3 grid-cols-1" data-testid="query-detail-dialog">
          {loading || !detail ? (
            <>
              <DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader>
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-6 flex-wrap">
                  <span>{detail.query_no}</span>
                  <StatusBadge status={detail.status} />
                </DialogTitle>
              </DialogHeader>

              <div className="text-xs text-neutral-600 space-y-0.5" data-testid="query-detail-meta">
                <div className="font-semibold text-neutral-900 text-sm break-words">{detail.subject}</div>
                <div>{detail.reason_label}</div>
                <div>{detail.distributor_name}{detail.distributor_code ? ` (${detail.distributor_code})` : ""}</div>
                {(detail.from_date || detail.to_date) && (
                  <div className="flex items-center gap-1">
                    <CalendarRange className="h-3 w-3" /> {fmtYmd(detail.from_date)} to {fmtYmd(detail.to_date)}
                  </div>
                )}
                <div>
                  Raised by {detail.created_by_name}
                  {detail.raised_on_behalf && <span className="text-neutral-400"> ({detail.created_by_role}, on behalf of the distributor)</span>}
                  {" · "}{fmtDateTime(detail.created_at)}
                </div>
                {detail.assigned_to_name && <div>Assigned to {detail.assigned_to_name}</div>}
              </div>

              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2">
                <div className="text-sm text-neutral-800 whitespace-pre-wrap break-words">{detail.description}</div>
                {(detail.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {detail.attachments.map((a) => <AttachmentChip key={a.id} att={a} />)}
                  </div>
                )}
              </div>

              <div className="space-y-2" data-testid="query-thread">
                {(detail.messages || []).map((m) => {
                  if (m.kind === "event") {
                    return (
                      <div key={m.id} className="text-center text-[11px] text-neutral-400 px-4 break-words">
                        {m.body}{m.sender_name ? ` · ${m.sender_name}` : ""} · {fmtDateTime(m.created_at)}
                      </div>
                    );
                  }
                  const mine = m.sender_side === "company";
                  return (
                    <div key={m.id} className={"flex " + (mine ? "justify-end" : "justify-start")}>
                      <div className={"max-w-[85%] rounded-2xl px-3 py-2 space-y-1.5 " +
                        (mine ? "bg-[#6D2C91]/10 rounded-br-sm" : "bg-neutral-100 rounded-bl-sm")}>
                        <div className="text-[10px] text-neutral-500">
                          <span className="font-semibold text-neutral-700">{m.sender_name}</span> · {fmtDateTime(m.created_at)}
                        </div>
                        {m.body && <div className="text-sm text-neutral-800 whitespace-pre-wrap break-words">{m.body}</div>}
                        {(m.attachments || []).length > 0 && (
                          <div className="flex flex-col gap-1">
                            {m.attachments.map((a) => <AttachmentChip key={a.id} att={a} />)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {hasActions && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {perms.can_start && (
                    <Button size="sm" variant="outline" disabled={acting} onClick={() => runAction("start")}
                            className="text-sky-700 border-sky-200" data-testid="query-action-start">
                      <Play className="h-4 w-4 mr-1" /> Start
                    </Button>
                  )}
                  {perms.can_resolve && (
                    <Button size="sm" disabled={acting} onClick={() => { setNote(""); setActionDlg("resolve"); }}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="query-action-resolve">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Mark resolved
                    </Button>
                  )}
                  {perms.can_close && (
                    <Button size="sm" disabled={acting} onClick={() => { setNote(""); setActionDlg("close"); }}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="query-action-close">
                      <Lock className="h-4 w-4 mr-1" /> Close
                    </Button>
                  )}
                  {perms.can_reopen && (
                    <Button size="sm" variant="outline" disabled={acting} onClick={() => { setNote(""); setActionDlg("reopen"); }}
                            className="text-rose-700 border-rose-200" data-testid="query-action-reopen">
                      <RotateCcw className="h-4 w-4 mr-1" /> Reopen
                    </Button>
                  )}
                </div>
              )}

              {perms.can_message ? (
                <div className="border-t border-neutral-200 pt-3 space-y-2" data-testid="query-reply-box">
                  <Textarea value={body} maxLength={4000} rows={3} disabled={sending}
                            onChange={(e) => setBody(e.target.value)} placeholder="Write a reply…"
                            data-testid="query-reply-body" />
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <FilePicker files={files} onChange={setFiles} disabled={sending} testid="query-reply-files" />
                    </div>
                    <Button className="tulsi-gradient text-white" onClick={send} disabled={sending}
                            data-testid="query-reply-send">
                      {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                      Send
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl bg-neutral-100 text-neutral-600 text-xs px-3 py-2" data-testid="query-readonly">
                  {detail.status === "closed"
                    ? `This query is closed${detail.closed_by === "system" ? " (automatically, 7 days after it was resolved)" : ""}.`
                    : "You have view-only access to this query."}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!actionDlg} onOpenChange={(v) => !v && !acting && setActionDlg(null)}>
        <DialogContent className="max-w-sm" data-testid="query-action-dialog">
          <DialogHeader>
            <DialogTitle>{actionDlg ? ACTION_COPY[actionDlg].title : ""}</DialogTitle>
          </DialogHeader>
          {actionDlg === "close" && (
            <p className="text-sm text-neutral-600">Close it if your query has been answered. You won't be able to reply after closing.</p>
          )}
          <Textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)}
                    placeholder={actionDlg ? ACTION_COPY[actionDlg].placeholder : ""}
                    data-testid="query-action-note" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActionDlg(null)} disabled={acting}>Cancel</Button>
            <Button onClick={confirmAction} disabled={acting} className="tulsi-gradient text-white"
                    data-testid="query-action-confirm">
              {acting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionDlg ? ACTION_COPY[actionDlg].confirm : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function QueryTracker({ mode = "portal" }) {
  const isAdmin = mode === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("id") || "";

  const [status, setStatus] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [reasonFilter, setReasonFilter] = useState(ALL_REASONS);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [myDistributors, setMyDistributors] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const reqSeq = useRef(0);
  const pageRef = useRef(1);
  pageRef.current = page;

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    // Admins also filter by deactivated reasons, whose old queries keep the code.
    api.get("/distributor-queries/reasons", { params: isAdmin ? { include_inactive: true } : {} })
      .then((r) => setReasons(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
    if (!isAdmin) {
      api.get("/distributor-queries/my-distributors")
        .then((r) => setMyDistributors(r.data.items || []))
        .catch((e) => toast.error(formatApiError(e)));
    }
  }, [isAdmin]);

  // With pageNum 1, `pages` re-fetches that many pages in one call so a refresh keeps "Load more" results.
  const load = useCallback(async (pageNum = 1, { silent = false, pages = 1 } = {}) => {
    const seq = ++reqSeq.current;
    if (pageNum > 1) setLoadingMore(true);
    else if (!silent) setLoading(true);
    try {
      const params = pageNum > 1
        ? { page: pageNum, page_size: PAGE_SIZE }
        : { page: 1, page_size: PAGE_SIZE * pages };
      if (status) params.status = status;
      if (q) params.q = q;
      if (isAdmin && reasonFilter !== ALL_REASONS) params.reason_code = reasonFilter;
      const { data } = await api.get("/distributor-queries", { params });
      if (seq !== reqSeq.current) return;
      const fresh = data.items || [];
      // Offset paging over last_activity_at shifts rows between loads; drop ones already shown.
      setItems((prev) => {
        if (pageNum === 1) return fresh;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...fresh.filter((n) => !seen.has(n.id))];
      });
      setTotal(data.total || 0);
      setCounts(data.counts || {});
      setPage(pageNum > 1 ? pageNum : pages);
    } catch (e) {
      if (seq === reqSeq.current) toast.error(formatApiError(e));
    } finally {
      if (seq === reqSeq.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [status, q, reasonFilter, isAdmin]);

  useEffect(() => { load(1); }, [load]);

  const openQuery = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", id);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeQuery = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const refreshSilently = useCallback(() => {
    load(1, { silent: true, pages: Math.min(pageRef.current, MAX_REFRESH_PAGES) });
  }, [load]);

  const onCreated = (d) => {
    setNewOpen(false);
    refreshSilently();
    openQuery(d.id);
  };

  // A dot flags threads where the other side spoke last.
  const otherSide = isAdmin ? "distributor" : "company";
  const showDistributor = mode !== "portal";
  const canRaise = !isAdmin && myDistributors.length >= 1;

  const newButton = canRaise ? (
    <Button className="tulsi-gradient text-white" onClick={() => setNewOpen(true)} data-testid="query-new-btn">
      <Plus className="h-4 w-4 mr-1.5" /> New query
    </Button>
  ) : null;

  return (
    <div className={isAdmin ? "space-y-4" : "p-4 pb-24 space-y-4"} data-testid={`query-tracker-${mode}`}>
      {isAdmin ? (
        <PageHeader
          breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Distributor Queries" }]}
          title="Distributor Queries"
          subtitle="Ledger requests, transport, damage, claims and documents raised by distributors and the field team."
          testid="query-tracker-header"
        />
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-extrabold text-neutral-900 flex items-center gap-2">
              <MessagesSquare className="h-5 w-5 text-[#6D2C91]" /> Query Tracker
            </h1>
            <p className="text-xs text-neutral-500">
              Ask for ledgers or documents, or report transport, damage and claim issues, and track the reply here.
            </p>
          </div>
          {newButton && <div className="shrink-0">{newButton}</div>}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((s) => (
          <button key={s || "all"} type="button" onClick={() => setStatus(s)}
                  data-testid={`query-filter-${s || "all"}`}
                  className={
                    "rounded-full border px-2.5 py-1 text-xs font-medium " +
                    (status === s ? "bg-[#6D2C91] border-[#6D2C91] text-white" : "bg-white border-neutral-200 text-neutral-700")
                  }>
            {s ? STATUS_LABEL[s] : "All"}
            <span className={"ml-1 " + (status === s ? "text-white/80" : "text-neutral-400")}>
              {counts[s || "all"] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} className="pl-8"
                 placeholder={showDistributor ? "Search query no, subject, distributor…" : "Search query no or subject…"}
                 data-testid="query-search" />
        </div>
        {isAdmin && (
          <Select value={reasonFilter} onValueChange={setReasonFilter}>
            <SelectTrigger className="sm:w-56" data-testid="query-reason-filter">
              <SelectValue placeholder="All reasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_REASONS}>All reasons</SelectItem>
              {reasons.map((r) => (
                <SelectItem key={r.code} value={r.code}>{r.label}{r.is_active === false ? " (inactive)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-sm text-neutral-500" data-testid="query-empty">
          {status || q || reasonFilter !== ALL_REASONS
            ? "No queries match these filters."
            : canRaise ? "No queries yet. Tap \"New query\" to ask the team for help." : "No queries yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <Card key={it.id} role="button" tabIndex={0}
                  onClick={() => openQuery(it.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") openQuery(it.id); }}
                  className="border-neutral-200 rounded-2xl cursor-pointer hover:bg-neutral-50 active:bg-neutral-50"
                  data-testid={`query-row-${it.id}`}>
              <CardContent className="p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{it.query_no}</span>
                    {it.last_message_side === otherSide && it.status !== "closed" && (
                      <span className="h-2 w-2 rounded-full bg-[#E5197F] shrink-0" title="New reply"
                            data-testid={`query-row-dot-${it.id}`} />
                    )}
                  </div>
                  <StatusBadge status={it.status} />
                </div>
                <div className="text-xs text-neutral-800 truncate">
                  <span className="text-neutral-500">{it.reason_label}</span> · {it.subject}
                </div>
                {showDistributor && (
                  <div className="text-[11px] text-neutral-500 truncate">
                    {it.distributor_name}{it.distributor_code ? ` (${it.distributor_code})` : ""}
                  </div>
                )}
                <div className="flex items-center gap-3 text-[11px] text-neutral-400">
                  <span>{relTime(it.last_activity_at || it.created_at)}</span>
                  <span className="flex items-center gap-0.5"><MessageSquare className="h-3 w-3" /> {it.message_count || 0}</span>
                  <span className="flex items-center gap-0.5"><Paperclip className="h-3 w-3" /> {it.attachment_count || 0}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length < total && (
            <Button variant="outline" className="w-full" onClick={() => load(page + 1)} disabled={loadingMore}
                    data-testid="query-load-more">
              {loadingMore && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Load more ({items.length} of {total})
            </Button>
          )}
        </div>
      )}

      <NewQueryDialog open={newOpen} onOpenChange={setNewOpen}
                      distributors={myDistributors} reasons={reasons} onCreated={onCreated} />
      <QueryDetailDialog queryId={openId} onClose={closeQuery} onChanged={refreshSilently} />
    </div>
  );
}
